import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
  onIdTokenChanged,
  User as FirebaseUser,
  updateProfile,
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { setCurrentUser, clearCurrentUser, UserRole } from '../data/session';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/v1';
const FRONTEND_URL = window.location.origin || 'http://localhost:3001';

// Continue URL after Firebase action. With a custom action URL configured
// in Firebase Console, Firebase calls our /auth/action page directly
// (mode + oobCode in query) and ignores this. We still pass it for the
// fallback case where Console hasn't been configured yet.
const VERIFY_RETURN  = `${FRONTEND_URL}/auth/action`;
const RESET_RETURN   = `${FRONTEND_URL}/auth/action`;

export type Provider = 'password' | 'google';
export type SignupRole = 'founder' | 'partner' | 'associate' | 'investor' | 'mentor';

export interface SignupPayload {
  // Common
  name: string;
  userType: SignupRole;
  inviteToken?: string;

  // Email/password only
  email?: string;
  password?: string;

  // Founder
  plan?: 'solo' | 'team';
  stage?: string;
  startupName?: string;

  // Partner
  institution?: string;
  studentCount?: string;

  // Associate
  organization?: string;
  purpose?: string;

  // Investor
  fundName?: string;
  checkSize?: string;

  // Partner / Associate / Investor share titleRole
  titleRole?: string;

  // Google profile (auto-filled from Google account, not entered by user)
  avatarUrl?: string;

  // Mentor (when accepting an invite)
  expertise?: string[];
  specialties?: string[];
  experience?: string;
}

export interface BackendUser {
  _id: string;
  firebaseUid: string;
  email: string;
  emailVerified: boolean;
  name: string;
  avatar: string;
  userType: UserRole;
  role: UserRole;          // alias for legacy frontend code
  status: 'active' | 'pending' | 'approved' | 'rejected' | 'suspended';
  bio?: string;
  company?: string;
  title?: string;
  region?: string;
  industry?: string[];
  startupId?: string;
  isFocalTrainee?: boolean;
  permissions?: string[];
  expertise?: string[];
  specialties?: string[];
  experience?: string;
  preferences?: any;
  founder?: any;
  mentor?: any;
  partner?: any;
  associate?: any;
  investor?: any;
  admin?: any;
}

export interface AuthResponse {
  user: BackendUser;
  requiresEmailVerification: boolean;
  provider: Provider;
}

/* ──────────────────────────  Token helpers  ────────────────────────── */

export const getIdToken = async (forceRefresh = false): Promise<string | null> => {
  const u = auth.currentUser;
  if (!u) return null;
  return u.getIdToken(forceRefresh);
};

export const onTokenChange = (cb: (user: FirebaseUser | null) => void) =>
  onIdTokenChanged(auth, cb);

/* ──────────────────────────  HTTP helpers  ─────────────────────────── */

async function apiPost<T = any>(path: string, body?: any, options?: { auth?: boolean }): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (options?.auth !== false) {
    const tok = await getIdToken();
    if (tok) headers.Authorization = `Bearer ${tok}`;
  }
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  return parseResponse<T>(res);
}

async function apiGet<T = any>(path: string, options?: { auth?: boolean }): Promise<T> {
  const headers: Record<string, string> = {};
  if (options?.auth !== false) {
    const tok = await getIdToken();
    if (tok) headers.Authorization = `Bearer ${tok}`;
  }
  const res = await fetch(`${API_URL}${path}`, { method: 'GET', headers });
  return parseResponse<T>(res);
}

async function apiPut<T = any>(path: string, body?: any): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const tok = await getIdToken();
  if (tok) headers.Authorization = `Bearer ${tok}`;
  const res = await fetch(`${API_URL}${path}`, {
    method: 'PUT',
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  return parseResponse<T>(res);
}

async function apiDelete<T = any>(path: string): Promise<T> {
  const headers: Record<string, string> = {};
  const tok = await getIdToken();
  if (tok) headers.Authorization = `Bearer ${tok}`;
  const res = await fetch(`${API_URL}${path}`, { method: 'DELETE', headers });
  return parseResponse<T>(res);
}

async function apiPatch<T = any>(path: string, body?: any): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const tok = await getIdToken();
  if (tok) headers.Authorization = `Bearer ${tok}`;
  const res = await fetch(`${API_URL}${path}`, { method: 'PATCH', headers, body: JSON.stringify(body) });
  return parseResponse<T>(res);
}

async function parseResponse<T>(res: Response): Promise<T> {
  const text = await res.text();
  let body: any = null;
  try { body = text ? JSON.parse(text) : null; } catch { /* keep as text */ }
  if (!res.ok) {
    const err: any = new Error(body?.error?.message || res.statusText || 'Request failed');
    err.status = res.status;
    err.code = body?.error?.code;
    err.details = body?.error?.details;
    throw err;
  }
  return body as T;
}

/* ──────────────────────────  Error mapping  ────────────────────────── */

export function friendlyAuthError(err: any): string {
  if (!err) return '';
  const code = err.code as string | undefined;
  const rawMsg = (err.message as string | undefined) || '';

  switch (code) {
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
    case 'auth/user-not-found':
      return 'Wrong email or password.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Try signing in.';
    case 'auth/invalid-email':
      return 'That email address looks invalid.';
    case 'auth/missing-email':
      return 'Please enter an email address.';
    case 'auth/missing-password':
      return 'Please enter a password.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/password-does-not-meet-requirements': {
      // Firebase puts the active rules in `Missing password requirements: [...]`.
      const m = rawMsg.match(/\[(.*?)\]/);
      if (m && m[1]) {
        const rules = m[1]
          .split(/\s*,\s*/)
          .map(r => r.replace(/^Password\s+/i, ''))
          .filter(Boolean);
        return rules.length
          ? `Password ${rules.join(', ')}.`
          : 'Password is too weak. Try a longer mix of letters, numbers, and symbols.';
      }
      return 'Password is too weak. Try a longer mix of letters, numbers, and symbols.';
    }
    case 'auth/too-many-requests':
      return 'Too many attempts — please wait a minute and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return ''; // user just closed the popup; no error to show
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in popup — allow popups for this site and try again.';
    case 'auth/network-request-failed':
      return 'Network error — check your connection and try again.';
    case 'auth/account-exists-with-different-credential':
      return 'An account with this email exists, but it was created with a different sign-in method. Try signing in another way.';
    case 'auth/expired-action-code':
      return 'This link has expired — request a new one.';
    case 'auth/invalid-action-code':
      return 'This link is invalid or has already been used.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Contact support.';

    case 'EMAIL_NOT_VERIFIED':
      return 'Please verify your email before continuing.';
    case 'NO_ACCOUNT':
      return "We don't have an account for this Firebase user — please sign up.";
    case 'ACCOUNT_DELETED':
      return 'This account has been deleted.';
    case 'INVITE_EMAIL_MISMATCH':
      return 'You signed in with a different email than the invite was sent to.';
    case 'INVITE_EXPIRED':
      return 'This invite has expired — ask your admin to resend it.';
    case 'INVITE_NOT_PENDING':
      return 'This invite has already been used or revoked.';
    case 'INVITE_NOT_FOUND':
    case 'INVALID_INVITE':
      return 'Invite link is invalid.';
    case 'INVITE_REQUIRED':
      return 'Mentor accounts can only be created via an invite link.';
    case 'NOT_FOUNDER':
      return 'Only founders can select a plan.';
    case 'INVALID_PLAN':
      return 'Please pick either the Solo or Team plan.';
  }

  // Strip the "Firebase: …(auth/…)." wrapper if it slipped through.
  const cleaned = rawMsg
    .replace(/^Firebase:\s*/i, '')
    .replace(/\s*\(auth\/[^)]+\)\.?$/i, '')
    .trim();
  return cleaned || 'Something went wrong. Please try again.';
}

/* ──────────────────────────  Routing helper  ───────────────────────── */

export interface RouteUser {
  role: string;
  status?: string;
  founder?:   { plan?: 'solo' | 'team' | null };
  partner?:   { institution?: string; titleRole?: string; studentCount?: string };
  associate?: { organization?: string; titleRole?: string; purpose?: string };
  investor?:  { fundName?: string; titleRole?: string; checkSize?: string };
}

const nonEmpty = (s?: string | null) => typeof s === 'string' && s.trim().length > 0;

/**
 * True when the role-specific subdoc is fully populated for a partner /
 * associate / investor. Founder / mentor / admin are always considered
 * complete (founder is gated by /select-plan instead; mentor and admin have
 * no required role-specific fields beyond what registration captures).
 */
export function isProfileComplete(user: RouteUser): boolean {
  if (user.role === 'partner') {
    const p = user.partner || {};
    return nonEmpty(p.institution) && nonEmpty(p.titleRole) && nonEmpty(p.studentCount);
  }
  if (user.role === 'associate') {
    const a = user.associate || {};
    return nonEmpty(a.organization) && nonEmpty(a.titleRole) && nonEmpty(a.purpose);
  }
  if (user.role === 'investor') {
    const i = user.investor || {};
    return nonEmpty(i.fundName) && nonEmpty(i.titleRole) && nonEmpty(i.checkSize);
  }
  return true;
}

export function routeAfterAuth(
  user: RouteUser,
  opts: { requiresEmailVerification?: boolean } = {},
): string {
  if (opts.requiresEmailVerification) return '/verify';
  if (['partner', 'associate', 'investor'].includes(user.role) && !isProfileComplete(user)) {
    return '/complete-profile';
  }
  const status = user.status;
  if (status === 'pending' || status === 'rejected' || status === 'suspended') {
    return '/pending';
  }
  if (user.role === 'founder' && (!user.founder || !user.founder.plan)) {
    return '/select-plan';
  }
  return `/app/dashboard/${user.role}`;
}

/* ──────────────────────────  Public API  ───────────────────────────── */

function applyToSession(user: BackendUser) {
  setCurrentUser({
    _id: user._id,
    name: user.name,
    role: user.role,
    avatar: user.avatar,
    company: user.company,
    title: user.title,
    bio: user.bio,
    startupId: user.startupId,
    isFocalTrainee: user.isFocalTrainee,
    permissions: user.permissions,
    expertise: user.expertise,
    specialties: user.specialties,
    experience: user.experience,
    region: user.region,
    industry: user.industry,
    preferences: user.preferences,
    plan: user.founder && user.founder.plan,
    status: user.status,
    founder:   user.founder,
    partner:   user.partner,
    associate: user.associate,
    investor:  user.investor,
  });
}

/** Set/change the founder's plan via the backend. */
export async function selectPlan(plan: 'solo' | 'team'): Promise<BackendUser> {
  const r = await apiPost<{ user: BackendUser }>('/users/me/select-plan', { plan });
  applyToSession(r.user);
  return r.user;
}

function buildRegisterBody(payload: SignupPayload, fallbackName?: string) {
  return {
    name: payload.name || fallbackName || '',
    avatarUrl: payload.avatarUrl || '',
    userType: payload.userType,
    inviteToken: payload.inviteToken,
    plan: payload.plan,
    stage: payload.stage,
    startupName: payload.startupName,
    institution: payload.institution,
    titleRole: payload.titleRole,
    studentCount: payload.studentCount,
    organization: payload.organization,
    purpose: payload.purpose,
    fundName: payload.fundName,
    checkSize: payload.checkSize,
    expertise: payload.expertise,
    specialties: payload.specialties,
    experience: payload.experience,
  };
}

/** Run only the Google popup (no backend call). */
export async function signInWithGooglePopup(): Promise<FirebaseUser> {
  const cred = await signInWithPopup(auth, googleProvider);
  return cred.user;
}

/**
 * Call /v1/auth/register for whoever is currently signed in to Firebase.
 * Falls back to /v1/auth/session if the backend says the user already exists.
 */
export async function registerCurrentUser(payload: SignupPayload): Promise<AuthResponse> {
  if (!auth.currentUser) throw new Error('No active Firebase user — sign in first');

  let result: AuthResponse;
  try {
    result = await apiPost<AuthResponse>('/auth/register', buildRegisterBody(payload, auth.currentUser.displayName || ''));
  } catch (err: any) {
    if (err.code === 'USER_EXISTS' || err.code === 'DUPLICATE_KEY' || err.status === 409) {
      const session = await apiPost<AuthResponse>('/auth/session');
      applyToSession(session.user);
      return session;
    }
    throw err;
  }

  await getIdToken(true);
  applyToSession(result.user);
  return result;
}

/** Email/password signup. Triggers Firebase verification email. */
export async function signupWithEmail(email: string, password: string, payload: SignupPayload): Promise<AuthResponse> {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  if (payload.name && cred.user) {
    try { await updateProfile(cred.user, { displayName: payload.name }); } catch { /* non-fatal */ }
  }

  try {
    await sendEmailVerification(cred.user, { url: VERIFY_RETURN, handleCodeInApp: false });
  } catch (e) {
    console.warn('sendEmailVerification failed', e);
  }

  return registerCurrentUser(payload);
}

/** Google signup (single shot — popup + register). Name and avatar are auto-filled from the Google account. */
export async function signupWithGoogle(payload: SignupPayload): Promise<AuthResponse> {
  await signInWithGooglePopup();
  // Enrich payload with Google profile data so the backend stores them immediately
  const gUser = auth.currentUser;
  if (gUser) {
    if (!payload.name) payload.name = gUser.displayName || '';
    if (!payload.avatarUrl) payload.avatarUrl = gUser.photoURL || '';
  }
  return registerCurrentUser(payload);
}

/** Email/password login. */
export async function loginWithEmail(email: string, password: string): Promise<AuthResponse> {
  await signInWithEmailAndPassword(auth, email, password);
  const result = await apiPost<AuthResponse>('/auth/session');
  applyToSession(result.user);
  return result;
}

/** Re-fetch the backend session for the currently signed-in Firebase user. */
export async function refreshSession(): Promise<AuthResponse | null> {
  if (!auth.currentUser) return null;
  await getIdToken(true); // pull a fresh token in case claims changed
  const result = await apiPost<AuthResponse>('/auth/session');
  applyToSession(result.user);
  return result;
}

/** Google login. Throws code=NO_ACCOUNT if user hasn't registered yet. */
export async function loginWithGoogle(): Promise<AuthResponse> {
  await signInWithPopup(auth, googleProvider);
  const result = await apiPost<AuthResponse>('/auth/session');
  applyToSession(result.user);
  return result;
}

/** Reload the current Firebase user and return whether their email is now verified. */
export async function reloadAndCheckVerified(): Promise<boolean> {
  if (!auth.currentUser) return false;
  await auth.currentUser.reload();
  // Force token refresh so the next backend call sees email_verified: true
  await getIdToken(true);
  return !!auth.currentUser.emailVerified;
}

/** Resend the Firebase verification email to the currently signed-in user. */
export async function resendVerificationEmail(): Promise<void> {
  if (!auth.currentUser) throw new Error('No active Firebase user');
  await sendEmailVerification(auth.currentUser, { url: VERIFY_RETURN, handleCodeInApp: false });
}

/** Forgot password — Firebase-driven, no backend call. */
export async function forgotPassword(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email, { url: RESET_RETURN, handleCodeInApp: false });
}

/** Sign out everywhere. */
export async function logout(): Promise<void> {
  try { await apiPost('/auth/logout'); } catch { /* best-effort */ }
  try { await signOut(auth); } catch { /* ignore */ }
  clearCurrentUser();
}

/* ──────────────────────────  Misc fetch helpers  ───────────────────── */

export const apiFetch = { get: apiGet, post: apiPost, put: apiPut, delete: apiDelete, patch: apiPatch };

/** Validate a mentor/investor invite token (public endpoint — no auth). */
export async function validateInviteToken(token: string): Promise<{ role: SignupRole; email: string; name: string; expiresAt: string }> {
  return apiGet(`/invites/validate/${encodeURIComponent(token)}`, { auth: false });
}
